import { enrichResult } from "./enrich-result";
import { requestOutcome } from "./request-outcome";
import type {
  EditorDocument,
  EnrichedEditorDocument,
  EnrichedMessage,
  EnrichedMessagePage,
  EnrichedTurnActivity,
  Message,
  MessagePage,
} from "./types";

const STAGE_LABELS = {
  reading: "Reading your payroll information",
  planning: "Working out the change",
  editing: "Making the change",
  checking: "Checking the proposed rules",
  calculating: "Comparing payroll calculations",
  saving: "Saving the proposal",
  documenting: "Updating your written rules",
} as const;

const REQUEST_OUTCOMES = {
  completed: "Request completed",
  failed: "Request failed",
  cancelled: "Request cancelled",
} as const;

const enrichTurnActivity = (
  request: Message | null | undefined,
  active: boolean,
  outcomeTitle?: string,
  startedAt?: string,
  writing = false,
): EnrichedTurnActivity | null => {
  if (!request) return null;
  const status = request.status;
  const outcome =
    status === "completed" || status === "failed" || status === "cancelled"
      ? (outcomeTitle ?? REQUEST_OUTCOMES[status])
      : undefined;
  const running = active && !outcome && status !== "queued";
  const steps = request.steps ?? [];
  return {
    requestId: request.id,
    status,
    ended: !!outcome,
    running,
    startedAt,
    label:
      outcome ?? (writing ? "Writing your reply…" : progressLabel(request)),
    detail:
      status === "waiting"
        ? "You can leave this page. Your request will continue when the check finishes."
        : running
          ? steps.at(-1)?.detail
          : null,
    steps: steps
      .map((step, index) => {
        const label =
          STAGE_LABELS[step.stage as keyof typeof STAGE_LABELS] ??
          "Working on your request";
        const count = step.count && step.count > 1 ? ` ×${step.count}` : "";
        return {
          ...step,
          key: `${step.stage}-${index + 1}`,
          label: `${label}${count}`,
          statusLabel:
            step.status === "failed"
              ? "Failed"
              : step.status === "completed"
                ? "Completed"
                : running && index === steps.length - 1
                  ? "In progress"
                  : "Stopped",
        };
      })
      .reverse(),
  };
};

const progressLabel = (request: Message | null | undefined) => {
  if (request?.status === "queued") return "Waiting to start…";
  if (request?.status === "waiting") return "Payroll check in progress…";
  const stage = request?.progress?.stage;
  const label =
    (stage && STAGE_LABELS[stage as keyof typeof STAGE_LABELS]) ??
    STAGE_LABELS.reading;
  return `${label}…`;
};

export const enrichMessagePage = (page: MessagePage): EnrichedMessagePage => {
  let requestContent = "";
  const source = page.messages.filter((message) => !message.attachment_upload);
  const preview = page.reply_preview;
  if (
    preview?.content.trim() &&
    page.latest_request?.id === preview.request_id &&
    page.latest_request.status === "running" &&
    !source.some(
      (message) =>
        message.role === "assistant" &&
        message.request_id === preview.request_id,
    )
  ) {
    source.push({
      id: `reply:${preview.request_id}`,
      sequence: (source.at(-1)?.sequence ?? 0) + 1,
      role: "assistant",
      content: preview.content,
      request_id: preview.request_id,
      created_at: page.latest_request.created_at,
      streaming: true,
    });
  }
  const messages = source.map((message) => {
    if (message.role === "user") requestContent = message.content;
    return {
      ...message,
      requestContent,
      result: message.result ? enrichResult(message.result) : undefined,
    };
  });
  const requests = messages.filter((message) => message.role === "user");
  const replies = new Map<string, EnrichedMessage[]>();
  for (const message of messages) {
    if (!message.request_id) continue;
    const group = replies.get(message.request_id) ?? [];
    group.push(message);
    replies.set(message.request_id, group);
  }
  const outcomes = new Map(
    requests.map((request) => [
      request.id,
      requestOutcome(request, replies.get(request.id) ?? []),
    ]),
  );
  const clarifications = new Map(
    requests.map((request) => {
      const last = replies
        .get(request.id)
        ?.filter((message) => message.result)
        .at(-1);
      return [
        request.id,
        request.status === "completed" && last?.result?.kind === "clarification"
          ? last.id
          : undefined,
      ];
    }),
  );
  const requestIds = new Set(requests.map((request) => request.id));
  const isActive =
    !!page.workspace.active_request ||
    page.latest_request?.status === "queued" ||
    page.latest_request?.status === "running" ||
    page.latest_request?.status === "waiting";
  return {
    ...page,
    messages: messages
      .filter((message) => {
        if (!message.request_id || !requestIds.has(message.request_id))
          return true;
        const clarification = clarifications.get(message.request_id);
        if (message.role === "assistant" && clarification) return false;
        return !message.result || message.id === clarification;
      })
      .map((message) => ({
        ...message,
        outcome: outcomes.get(message.id),
        activity:
          message.role === "user"
            ? enrichTurnActivity(
                message.id === page.latest_request?.id
                  ? page.latest_request
                  : message,
                isActive && message.id === page.latest_request?.id,
                outcomes.get(message.id)?.title,
                page.workspace.active_request?.started_at,
                !!preview?.content && preview.request_id === message.id,
              )
            : undefined,
        hasRequestOutcome: !!outcomes.get(message.request_id ?? ""),
      })),
    isActive,
    activity: enrichTurnActivity(
      page.latest_request,
      isActive,
      outcomes.get(page.latest_request?.id ?? "")?.title,
      page.workspace.active_request?.started_at,
      !!preview?.content && preview.request_id === page.latest_request?.id,
    ),
    awaitingAnswer:
      !page.workspace.active_request &&
      !!clarifications.get(page.latest_request?.id ?? ""),
  };
};

export const enrichEditorDocuments = (
  documents: EditorDocument[],
): EnrichedEditorDocument[] =>
  documents.map((document) => ({
    ...document,
    errorMessage:
      document.status === "extraction_failed"
        ? document.error ||
          "This file could not be read. Remove it and upload a new copy."
        : undefined,
  }));
